<script setup lang="ts">
import { SearchRepositoriesDocument, SearchTeamsDocument, SearchTeamspacesDocument } from './GoToSearch.generated'

export type SearchType = 'team' | 'repository' | 'teamspace'

const props = defineProps<{
  objectType: SearchType
}>()

const emit = defineEmits<{
  clickLink: []
}>()

const { addRecentItem } = useRecentNavigation()

const searchTerm = ref('')
const debouncedSearchTerm = refDebounced(searchTerm, 300)

const { data: teams } = useQuery({
  query: SearchTeamsDocument,
  variables: computed(() => ({
    term: debouncedSearchTerm.value,
  })),
  paused: computed(() => props.objectType !== 'team'),
}, ({ data }) => data?.organization.teams.nodes)

const { data: repositories } = useQuery({
  query: SearchRepositoriesDocument,
  variables: computed(() => ({
    term: debouncedSearchTerm.value,
  })),
  paused: computed(() => props.objectType !== 'repository'),
}, ({ data }) => data?.organization.skillsRepositories.nodes)

const { data: teamspaces } = useQuery({
  query: SearchTeamspacesDocument,
  variables: computed(() => ({
    term: debouncedSearchTerm.value,
  })),
  paused: computed(() => props.objectType !== 'teamspace'),
}, ({ data }) => data?.workspaces.nodes)

function handleLinkClick(item: RecentNavigationItem) {
  addRecentItem(item)
  emit('clickLink')
}

const teamLinks = computed(() => teams.value?.map(team => ({
  label: team.name,
  to: { name: 'teams-teamId' as const, params: { teamId: team.id } },
})) ?? [])

const repositoryLinks = computed(() => repositories.value?.map(repo => ({
  label: `${repo.repository.owner}/${repo.repository.name}`,
  to: { name: 'repositories-gid' as const, params: { gid: repo.gid } },
})) ?? [])

const teamspaceLinks = computed(() => (teamspaces.value ?? [])
  .filter(teamspace => !teamspace.isGlobal)
  .map(teamspace => ({
    label: teamspace.name,
    to: { name: 'teamspace-workspaceId' as const, params: { workspaceId: teamspace.id } },
  })),
)

const links = computed(() => {
  if (props.objectType === 'team')
    return teamLinks.value
  if (props.objectType === 'repository')
    return repositoryLinks.value
  return teamspaceLinks.value
})

const linksWithHandler = computed(() => links.value.map(link => ({
  ...link,
  click: () => handleLinkClick(link),
})))

const placeholder = computed(() => {
  if (props.objectType === 'team')
    return 'Search teams'
  if (props.objectType === 'repository')
    return 'Search repositories'
  return 'Search teamspaces'
})
</script>

<template>
  <div>
    <UInput
      v-model="searchTerm"
      icon="light:magnifying-glass"
      :placeholder="placeholder"
      autofocus
    />
    <UVerticalNavigation
      class="mt-4"
      :links="linksWithHandler"
    />
  </div>
</template>
